import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import _defineProperty from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { makeReference } from '@apollo/client';
import { AppAlertsContext } from '@infl-ts/ui-app-alerts';
import { getLocale, offsetToUTCString } from '@infl-ts/util-date';
import { DE$ } from '@infl-ts/util-datum';
import { useFocusErrors } from '@infl-ts/util-react-hook-form';
import { combineMutationStatuses, useMutationMapper } from '@infl-ts/util-use-mutation-mapper';
import { useUnsavedChangesWarning } from '@infl-ts/util-use-unsaved-changes';
import { Box, FormControl } from '@mui/material';
import * as DE from '@nll/datum/lib/DatumEither';
import { format, formatISO, parseISO } from 'date-fns';
import * as A from 'fp-ts/Array';
import * as E from 'fp-ts/Either';
import { constant, constNull, constVoid, flow, identity, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import { useContext } from 'react';
import { get, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { findTimeZone, getUTCOffset, listTimeZones } from 'timezone-support';
import { AsyncButton, SaveStates } from '../../async-button';
import { AutocompleteFormField, TextFormField } from '../../form';
import { ImageUpload } from '../../image-upload';
import { useLanguages } from '../../language-selector/queries';
import { AboutYouBox } from './about-you-box';
import { imageRemoveFailure, imageSubmitFailure, submitFailure, submitSuccess } from './alerts';
import CustomField from './custom-field';
import { useEditProfile, useUpdateProfileImage } from './mutations';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var validTimezones = listTimeZones();
export var getAllTimezoneOptions = function getAllTimezoneOptions(currentTimezone) {
  var currentDay = new Date();
  return pipe(validTimezones, A.map(function (tz) {
    return pipe(findTimeZone(tz), function (tzInfo) {
      return getUTCOffset(currentDay, tzInfo);
    }, offsetToUTCString, function (prefix) {
      return {
        label: "".concat(prefix, " - ").concat(tz),
        value: tz
      };
    });
  }), currentTimezone && !validTimezones.includes(currentTimezone) ? A.prepend({
    label: currentTimezone,
    value: currentTimezone
  }) : identity);
};

var LanguageSelector = function LanguageSelector(_ref) {
  var control = _ref.control,
      errors = _ref.errors;
  return pipe(useLanguages(), DE.map(function (_) {
    return _.languages;
  }), DE.map(A.map(function (language) {
    return {
      label: language.displayName,
      value: language.code
    };
  })), DE$.getSuccessOrElse([]), function (languages) {
    return /*#__PURE__*/_jsx(FormControl, {
      sx: {
        width: 1,
        mb: 3
      },
      children: /*#__PURE__*/_jsx(AutocompleteFormField, {
        name: "language",
        control: control,
        label: "Language",
        options: languages,
        error: errors.language,
        errorMessage: "Choose a valid language."
      })
    });
  });
};

export var PureAboutYou = function PureAboutYou(_ref2) {
  var _getValues;

  var aboutYouForm = _ref2.aboutYouForm,
      handleSaveChanges = _ref2.handleSaveChanges,
      profileMutationStatus = _ref2.profileMutationStatus,
      profileImageMutationStatus = _ref2.profileImageMutationStatus,
      allTimezoneOptions = _ref2.allTimezoneOptions,
      profile = _ref2.profile,
      isDirty = _ref2.isDirty,
      langSelectorEnabled = _ref2.langSelectorEnabled;
  var control = aboutYouForm.control,
      handleSubmit = aboutYouForm.handleSubmit,
      getValues = aboutYouForm.getValues,
      watch = aboutYouForm.watch,
      errors = aboutYouForm.formState.errors;
  var Saving = SaveStates.Saving,
      Default = SaveStates.Default,
      Success = SaveStates.Success,
      Error = SaveStates.Error;
  var profileMutationValue = useMutationMapper(profileMutationStatus, function (_ref3) {
    var editProfile = _ref3.editProfile;
    return editProfile.__typename === 'EditProfileErrors' ? E.left(editProfile) : E.right(editProfile);
  }, [Saving, isDirty ? Default : Success, Error, Default]);
  var profileImageMutationValue = useMutationMapper(profileImageMutationStatus, function (_ref4) {
    var updateProfileImage = _ref4.updateProfileImage;
    return updateProfileImage.__typename === 'UpdateProfileImageOutput' ? E.right(updateProfileImage) : E.left(updateProfileImage);
  }, [Saving, isDirty ? Default : Success, Error, Default]);

  var _useFocusErrors = useFocusErrors(),
      questionRef = _useFocusErrors.questionRef,
      focusOnFirstError = _useFocusErrors.focusOnFirstError;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  return /*#__PURE__*/_jsxs(AboutYouBox, {
    questionRef: questionRef,
    "data-element": "AboutYouBox",
    "data-component": "PureAboutYou",
    "data-source-file": "about-you.tsx",
    children: [pipe(watch('avatarUrl'), function (watchAvatarUrl) {
      return /*#__PURE__*/_jsx(ImageUpload, {
        member: _objectSpread(_objectSpread({}, profile), {}, {
          avatarUrl: watchAvatarUrl
        }),
        onUpload: function onUpload(newAvatarUrl) {
          return aboutYouForm.setValue('avatarUrl', newAvatarUrl, {
            shouldDirty: true
          });
        }
      });
    }), /*#__PURE__*/_jsx(FormControl, {
      id: "name",
      sx: {
        width: 1,
        mb: 3
      },
      "data-element": "FormControl",
      "data-component": "PureAboutYou",
      "data-source-file": "about-you.tsx",
      children: /*#__PURE__*/_jsx(TextFormField, {
        name: "name",
        control: control,
        required: true,
        label: t('Name'),
        error: errors.name,
        "data-element": "TextFormField",
        "data-component": "PureAboutYou",
        "data-source-file": "about-you.tsx"
      })
    }), /*#__PURE__*/_jsx(FormControl, {
      id: "username",
      sx: {
        width: 1,
        mb: 3
      },
      "data-element": "FormControl",
      "data-source-file": "about-you.tsx",
      children: /*#__PURE__*/_jsx(TextFormField, {
        name: "username",
        control: control,
        required: true,
        label: t('Username'),
        error: errors.username,
        "data-element": "TextFormField",
        "data-source-file": "about-you.tsx"
      })
    }), /*#__PURE__*/_jsxs(Box, {
      sx: {
        display: 'flex',
        flexDirection: {
          xs: 'column',
          md: 'row'
        }
      },
      "data-element": "Box",
      "data-source-file": "about-you.tsx",
      children: [/*#__PURE__*/_jsx(FormControl, {
        id: "title",
        sx: {
          width: 1,
          mb: 2,
          mr: 3
        },
        "data-element": "FormControl",
        "data-source-file": "about-you.tsx",
        children: /*#__PURE__*/_jsx(TextFormField, {
          name: "title",
          control: control,
          label: t('Job Title'),
          error: errors.title,
          "data-element": "TextFormField",
          "data-source-file": "about-you.tsx"
        })
      }), /*#__PURE__*/_jsx(FormControl, {
        id: "company",
        sx: {
          width: 1,
          mb: 3
        },
        "data-element": "FormControl",
        "data-source-file": "about-you.tsx",
        children: /*#__PURE__*/_jsx(TextFormField, {
          name: "company",
          control: control,
          label: t('Company'),
          error: errors.company,
          "data-element": "TextFormField",
          "data-source-file": "about-you.tsx"
        })
      })]
    }), langSelectorEnabled ? /*#__PURE__*/_jsx(LanguageSelector, {
      control: control,
      errors: errors
    }) : null, /*#__PURE__*/_jsx(FormControl, {
      id: "timezone",
      sx: {
        width: 1,
        mb: 3
      },
      "data-element": "FormControl",
      "data-source-file": "about-you.tsx",
      children: /*#__PURE__*/_jsx(AutocompleteFormField, {
        name: "timezone",
        control: control,
        label: t('Timezone'),
        options: allTimezoneOptions,
        error: errors.timezone,
        errorMessage: "Choose a valid timezone.",
        "data-element": "AutocompleteFormField",
        "data-source-file": "about-you.tsx"
      })
    }), ((_getValues = getValues('customFields')) !== null && _getValues !== void 0 ? _getValues : []).map(function (field, index) {
      return /*#__PURE__*/_jsx(FormControl, {
        id: field.categoryID,
        sx: {
          width: 1,
          mb: 3
        },
        children: /*#__PURE__*/_jsx(CustomField, {
          customField: field.__typename === 'CustomFieldDate' ? _objectSpread(_objectSpread({}, field), {}, {
            value: field.value ? field.value : null
          }) : field,
          name: "customFields.".concat(index, ".value"),
          control: control,
          error: get(errors, "customFields.".concat(index, ".value"))
        })
      }, field.categoryID);
    }), /*#__PURE__*/_jsx(Box, {
      sx: {
        width: 1,
        display: 'flex',
        justifyContent: 'flex-end'
      },
      "data-element": "Box",
      "data-source-file": "about-you.tsx",
      children: /*#__PURE__*/_jsx(AsyncButton, {
        status: combineMutationStatuses([profileMutationValue, profileImageMutationValue]),
        onClick: handleSubmit(handleSaveChanges, focusOnFirstError),
        text: ['Saving...', 'Saved', 'Save changes', 'Save changes'],
        "data-element": "AsyncButton",
        "data-source-file": "about-you.tsx"
      })
    })]
  });
};

var getCustomFieldLabel = function getCustomFieldLabel(fieldType) {
  return fieldType === 'CustomFieldText' ? 'text' : fieldType === 'CustomFieldPickList' ? 'pickList' : fieldType === 'CustomFieldMultiSelect' ? 'multiSelect' : 'date';
};

var profileAttributes = function profileAttributes(aboutYouForm, language) {
  return {
    name: aboutYouForm.getValues('name'),
    username: aboutYouForm.getValues('username'),
    title: aboutYouForm.getValues('title'),
    company: aboutYouForm.getValues('company'),
    timezone: aboutYouForm.getValues('timezone'),
    language: aboutYouForm.getValues('language'),
    customFields: pipe(aboutYouForm.getValues('customFields'), O.fromNullable, O.map(A.map(function (f) {
      var _f$value;

      var label = getCustomFieldLabel(f.__typename);
      return f.__typename === 'CustomFieldMultiSelect' ? _defineProperty({}, label, {
        values: pipe((_f$value = f.value) !== null && _f$value !== void 0 ? _f$value : [], A.filterMap(function (value) {
          return pipe(f.valueOptions, A.findIndex(function (option) {
            return option === value;
          }));
        })),
        categoryID: f.categoryID
      }) : _defineProperty({}, label, {
        value: f.__typename === 'CustomFieldPickList' ? pipe(f.valueOptions, A.findIndex(function (option) {
          return option === f.value;
        }), O.getOrElseW(constNull)) : f.__typename === 'CustomFieldDate' ? f.value ? pipe(f.value, parseISO, function (date) {
          return format(date, 'yyyy-MM-dd', {
            locale: getLocale(language)
          });
        }) : null : f.value,
        categoryID: f.categoryID
      });
    })), O.toNullable)
  };
};

export var formatCustomFields = function formatCustomFields(customFields) {
  return pipe(customFields, O.fromNullable, O.map(A.map(function (field) {
    var _field$value, _field$values;

    return field.__typename === 'CustomFieldDate' ? _objectSpread(_objectSpread({}, field), {}, {
      value: field.value ? formatISO(parseISO(field.value)) : null
    }) : field.__typename === 'CustomFieldText' ? _objectSpread(_objectSpread({}, field), {}, {
      value: (_field$value = field.value) !== null && _field$value !== void 0 ? _field$value : ''
    }) : field.__typename === 'CustomFieldMultiSelect' ? _objectSpread(_objectSpread({}, field), {}, {
      value: (_field$values = field.values) !== null && _field$values !== void 0 ? _field$values : []
    }) : field;
  })), O.toNullable);
};

var useProps = function useProps(_ref7) {
  var profile = _ref7.profile,
      langSelectorEnabled = _ref7.langSelectorEnabled;
  var aboutYouForm = useForm({
    defaultValues: _objectSpread(_objectSpread({}, profile), {}, {
      language: profile.language.code,
      customFields: formatCustomFields(profile.customFields)
    })
  });

  var _useUnsavedChangesWar = useUnsavedChangesWarning(aboutYouForm.formState.isDirty),
      setDirtyState = _useUnsavedChangesWar.setDirtyState,
      isDirty = _useUnsavedChangesWar.isDirty;

  var _useContext = useContext(AppAlertsContext),
      showAlert = _useContext.showAlert;

  var _useTranslation2 = useTranslation(),
      t = _useTranslation2.t,
      i18n = _useTranslation2.i18n;

  var _useUpdateProfileImag = useUpdateProfileImage({
    onCompleted: E.matchW(function () {
      return showAlert(profile.avatarUrl ? imageSubmitFailure(t) : imageRemoveFailure(t));
    }, constVoid),
    update: function update(cache, data) {
      return pipe(data, E.match(constVoid, function (_ref8) {
        var updateProfileImage = _ref8.updateProfileImage;
        if (updateProfileImage.__typename !== 'UpdateProfileImageOutput') return;
        var url = updateProfileImage.url;
        ['Profile', 'Member'].forEach(function (type) {
          return cache.modify({
            id: cache.identify(makeReference("".concat(type, ":").concat(profile.id))),
            fields: {
              avatarUrl: constant(url)
            }
          });
        });
        aboutYouForm.reset(_objectSpread(_objectSpread({}, aboutYouForm.getValues()), {}, {
          avatarUrl: url
        }));
      }));
    }
  }),
      _useUpdateProfileImag2 = _slicedToArray(_useUpdateProfileImag, 2),
      updateProfileImage = _useUpdateProfileImag2[0],
      profileImageMutationStatus = _useUpdateProfileImag2[1];

  var _useEditProfile = useEditProfile({
    onCompleted: E.match(function () {
      return showAlert(submitFailure(handleSaveChanges, t));
    }, function (_ref9) {
      var editProfile = _ref9.editProfile;

      if (editProfile.__typename === 'EditProfileResult') {
        showAlert(submitSuccess(t));
        setDirtyState(false);
      } else {
        showAlert(submitFailure(handleSaveChanges, t));
      }
    }),
    update: function update(cache, data) {
      return pipe(data, E.match(constVoid, function (_ref10) {
        var editProfile = _ref10.editProfile;
        if (editProfile.__typename !== 'EditProfileResult') return;
        var _editProfile$result = editProfile.result,
            name = _editProfile$result.name,
            title = _editProfile$result.title,
            company = _editProfile$result.company,
            timezone = _editProfile$result.timezone,
            customFields = _editProfile$result.customFields;
        var rootQueryId = cache.identify(makeReference('ROOT_QUERY'));
        ['Profile', 'Member'].forEach(function (type) {
          return cache.modify({
            id: cache.identify(makeReference("".concat(type, ":").concat(profile.id))),
            fields: {
              name: constant(name),
              title: constant(title),
              company: constant(company),
              timezone: constant(timezone),
              customFields: constant(customFields)
            }
          });
        });
        cache.evict({
          id: rootQueryId,
          fieldName: 'availableChallenges'
        });
        cache.evict({
          id: rootQueryId,
          fieldName: 'savedForLaterChallenges'
        });
        cache.evict({
          id: rootQueryId,
          fieldName: 'pendingApprovalChallenges'
        });
        cache.evict({
          id: rootQueryId,
          fieldName: 'completedChallenges'
        });
        cache.gc();
        aboutYouForm.reset(_objectSpread(_objectSpread({}, aboutYouForm.getValues()), {}, {
          name: name,
          title: title,
          company: company,
          timezone: timezone,
          language: profile.language.code,
          customFields: formatCustomFields(customFields)
        }));
      }));
    }
  }),
      _useEditProfile2 = _slicedToArray(_useEditProfile, 2),
      editProfile = _useEditProfile2[0],
      profileMutationStatus = _useEditProfile2[1];

  var handleSaveChanges = function handleSaveChanges() {
    void updateProfileImage({
      variables: {
        input: {
          url: aboutYouForm.getValues('avatarUrl') || ''
        }
      }
    });
    void editProfile({
      variables: {
        input: profileAttributes(aboutYouForm, i18n.language)
      }
    });
  };

  return {
    aboutYouForm: aboutYouForm,
    profileMutationStatus: profileMutationStatus,
    profileImageMutationStatus: profileImageMutationStatus,
    handleSaveChanges: handleSaveChanges,
    allTimezoneOptions: getAllTimezoneOptions(profile.timezone),
    profile: profile,
    isDirty: isDirty,
    langSelectorEnabled: langSelectorEnabled
  };
};

export default Object.assign(flow(useProps, PureAboutYou), {
  displayName: 'AboutYou'
});